import { ReviewThreadStatus, type ReviewSubjectType } from "@/api/cle-api";
import type { ReviewThreadAssignment } from "@/components/pages/efteruddannelse/filters/ReviewThreadAssignmentFilter";
import FiltersEfteruddannelse, {
  type ReviewThreadFilterState,
} from "@/components/filter/FiltersEfteruddannelse";
import { ReviewThreadsNavHeader } from "@/components/pages/efteruddannelse/ReviewThreadsNavHeader";
import { ReviewThreadsTable } from "@/components/pages/efteruddannelse/ReviewThreadsTable";
import Spinner from "@/components/spinner/Spinner";
import { useReviewThreads } from "@/hooks/cle-hooks";
import PageTitle from "@/components/PageTitle";
import { ErrorBoundary } from "react-error-boundary";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getReviewThreadDetailPath } from "@/components/pages/efteruddannelse/utils/reviewThreadNavigation";
import ErrorFallback from "../../components/ErrorFallback";

const PAGE_SIZE = 20;

const defaultFilters: ReviewThreadFilterState = {
  statuses: [ReviewThreadStatus.OPEN],
  subjectTypes: [],
};

const Efteruddannelse = () => {
  const [filters, setFilters] =
    useState<ReviewThreadFilterState>(defaultFilters);
  const [page, setPage] = useState(1);

  const resetPage = () => setPage(1);

  const handleStatusToggle = (status: ReviewThreadStatus, checked: boolean) => {
    setFilters((current) => ({
      ...current,
      statuses: checked
        ? [...current.statuses, status]
        : current.statuses.filter((value) => value !== status),
    }));
    resetPage();
  };

  const handleSubjectTypeToggle = (
    subjectType: ReviewSubjectType,
    checked: boolean,
  ) => {
    setFilters((current) => ({
      ...current,
      subjectTypes: checked
        ? [...current.subjectTypes, subjectType]
        : current.subjectTypes.filter((value) => value !== subjectType),
    }));
    resetPage();
  };

  const handleOwnerChange = (owner?: { id: string; name: string }) => {
    setFilters((current) => ({
      ...current,
      ownerUserId: owner?.id,
      ownerDisplayName: owner?.name,
    }));
    resetPage();
  };

  const handleAssignmentChange = (assignment: ReviewThreadAssignment) => {
    setFilters((current) => ({
      ...current,
      assignedTo: assignment.assignedTo,
      assignedAdminEmail: assignment.assignedAdminEmail,
      assignedAdminDisplayName: assignment.assignedAdminDisplayName,
    }));
    resetPage();
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    resetPage();
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <PageTitle title="Efteruddannelse" />
      <div className="l-main-row">
        <FiltersEfteruddannelse
          filters={filters}
          onStatusToggle={handleStatusToggle}
          onSubjectTypeToggle={handleSubjectTypeToggle}
          onOwnerChange={handleOwnerChange}
          onAssignmentChange={handleAssignmentChange}
          onReset={handleReset}
        />
        <Main filters={filters} page={page} setPage={setPage} />
      </div>
    </ErrorBoundary>
  );
};

interface MainProps {
  filters: ReviewThreadFilterState;
  page: number;
  setPage: (page: number | ((current: number) => number)) => void;
}

const Main = ({ filters, page, setPage }: MainProps) => {
  const navigate = useNavigate();

  const { data, isLoading, error } = useReviewThreads({
    status: filters.statuses.length > 0 ? filters.statuses : undefined,
    subjectType:
      filters.subjectTypes.length > 0 ? filters.subjectTypes : undefined,
    ownerUserId: filters.ownerUserId,
    assignedAdminEmail: filters.assignedAdminEmail,
    assignedTo: filters.assignedTo,
    page,
    pageSize: PAGE_SIZE,
  });

  const threads = data?.items ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const pagination = {
    page,
    pageSize: PAGE_SIZE,
    totalCount,
    hasPreviousPage: page > 1,
    hasNextPage: page < totalPages,
  };

  return (
    <main className="l-main__content col overflow-hidden">
      <ReviewThreadsNavHeader
        totalCount={totalCount}
        page={pagination.page}
        pageSize={pagination.pageSize}
        hasPreviousPage={pagination.hasPreviousPage}
        hasNextPage={pagination.hasNextPage}
        onPreviousClick={() => setPage((current) => Math.max(1, current - 1))}
        onNextClick={() =>
          setPage((current) => Math.min(totalPages, current + 1))
        }
      />
      <div className="l-main__table px-2">
        {isLoading && !data ? (
          <Spinner label="Henter henvendelser ..." />
        ) : (
          <ReviewThreadsTable
            threads={threads}
            isLoading={isLoading}
            error={error}
            onViewThread={(thread) =>
              navigate(getReviewThreadDetailPath(thread), {
                state: { thread },
              })
            }
          />
        )}
      </div>
    </main>
  );
};

export default Efteruddannelse;
